import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Table from "../../components/Table";
import { format } from "date-fns";
import AddDialog from "../../components/Modal/AddModal";

// If these are custom inputs you already have, great.
// If not, replace with your own or native inputs.
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import { getBranchService } from "../../services/branchService";
import { useAuth } from "../../context/AuthContext";
import { getCoachTypeService } from "../../services/coachTypeService";
import { addEmployeeService, getEmployees, updateEmployeeService } from "../../services/employeeService";
import { API_ORIGIN } from "../../services/httpService";
import { showErrorToast } from "../../services/toastService";
import { FaEye, FaPencilAlt } from "react-icons/fa";
import { hasPermission } from "../../utils/permissions";
import { getPackages } from "../../services/packagesService";

type CommissionRuleForm = {
    package: string;
    allocationId: string;
    percentage: string | number;
};

type PackageOption = {
    _id: string;
    name: string;
    status?: boolean;
    isPtSession?: boolean;
    numberOfPtSessions?: number;
    revenueAllocations?: Array<{ _id: string; name: string; percentage: number }>;
};

const formatDate = (value?: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return format(d, "dd/MM/yyyy");
};

const StatusCell = ({ value }: { value: boolean }) => (
    <span className={`px-2 py-1 rounded-md ${value ? "text-green-500" : "text-red-500"}`}>
        {value ? "Active" : "Inactive"}
    </span>
);

const getSalaryComponents = (employee: any) => {
    if (employee?.salaryCalculationMode === "combined") {
        return {
            fixed: Boolean(employee.salaryFixedEnabled),
            hourly: Boolean(employee.salaryHourlyEnabled),
            percentage: Boolean(employee.salaryPercentageEnabled),
            session: Boolean(employee.salarySessionEnabled),
        };
    }

    const salaryType = employee?.salaryType ?? "fixed";
    return {
        fixed: salaryType === "fixed",
        hourly: salaryType === "hourly",
        percentage: salaryType === "percentage",
        session: false,
    };
};

const getPrimarySalaryType = (values: any) => {
    if (values.salaryFixedEnabled) return "fixed";
    if (values.salaryHourlyEnabled) return "hourly";
    if (values.salaryPercentageEnabled) return "percentage";
    return "fixed";
};

const formatMoney = (value: unknown) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "-";
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatSalaryMethods = (employee: any) => {
    const components = getSalaryComponents(employee);
    const labels = [
        components.fixed ? "Fixed" : "",
        components.hourly ? "Hourly" : "",
        components.percentage ? "Percentage" : "",
        components.session ? "Per Session" : "",
    ].filter(Boolean);

    return labels.length ? labels.join(" + ") : "None";
};

const DetailItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
        <dd className="mt-1 break-words text-sm text-gray-900">{value || "-"}</dd>
    </div>
);

const Coaches = () => {
    const [addCoach, setAddCoach] = useState(false);
    const [selectedCoach, setSelectedCoach] = useState<any | null>(null);
    const { user } = useAuth();
    const canEditEmployees = hasPermission(user, "employees.write");
    const [tableData, setTableData] = useState<any[]>([]);
    const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);

    // Safe defaults so .map doesn't crash if data hasn't loaded yet
    const [coachTypes, setCoachTypes] = useState<{ _id: string; name: string }[]>([]);
    const [branches, setBranchesType] = useState<{ _id: string; name: string }[]>([]);
    const [packages, setPackages] = useState<PackageOption[]>([]);

    // Profile image preview state
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setImageFile(file ?? null);
        if (file) {
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        } else {
            setImagePreview(null);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    const columns = [
        { Header: "First Name", accessor: "firstName" },
        { Header: "Last Name", accessor: "lastName" },
        {
            Header: "DOB",
            accessor: "DOB",
            Cell: ({ row }: { row: any }) => formatDate(row?.original?.DOB ?? row?.original?.birthday),
        },
        { Header: "Status", accessor: "status", Cell: StatusCell },
        { Header: "Mobile", accessor: "mobile" },
        { Header: "Gender", accessor: "gender" },
        {
            Header: "Type",
            accessor: "type.name",
            Cell: ({ row }: { row: any }) => row?.original?.type?.name ?? row?.original?.type ?? "-",
        },
        {
            Header: "Branch",
            accessor: "branch.name",
            Cell: ({ row }: { row: any }) => row?.original?.branch?.name ?? row?.original?.branch ?? "-",
        },
        {
            Header: "Salary Methods",
            accessor: "salaryType",
            Cell: ({ row }: { row: any }) => formatSalaryMethods(row?.original ?? {}),
        },
        {
            Header: "Details",
            accessor: "details",
            Cell: ({ row }: { row: any }) => (
                <button
                    type="button"
                    onClick={() => setSelectedCoach(row.original)}
                    className="rounded-md p-2 text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
                    title="View coach details"
                    aria-label={`View details for ${row.original?.firstName ?? ""} ${row.original?.lastName ?? ""}`.trim()}
                >
                    <FaEye size={18} />
                </button>
            ),
        },
        ...(canEditEmployees
            ? [{
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }) => (
                    <button
                        type="button"
                        onClick={() => handleEditEmployee(row.original)}
                        className="rounded-md p-2 text-blue-500 transition hover:bg-blue-50 hover:text-blue-700"
                        title="Edit coach"
                        aria-label={`Edit ${row.original?.firstName ?? ""} ${row.original?.lastName ?? ""}`.trim()}
                    >
                        <FaPencilAlt />
                    </button>
                ),
            }]
            : []),
    ];

    const openPopUp = () => {
        setEditingEmployeeId(null);
        setAddCoach(true);
    };

    // --------- FORMIK ----------
    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            DOB: "", // ISO date string: yyyy-MM-dd
            gender: "",
            bloodType: "",
            branch: "",
            email: "",
            mobile: "",
            emergencyContact: "",
            address: "",
            status: true, // default active
            type: "", // role/type (coach type id)
            salaryCalculationMode: "combined",
            salaryType: "fixed",
            salaryFixedEnabled: true,
            fixedSalary: "",
            salaryHourlyEnabled: false,
            hourlyRate: "",
            salaryPercentageEnabled: false,
            commissionPercentage: "",
            commissionRules: [] as CommissionRuleForm[],
            sessionDurationHours: "1",
            salarySessionEnabled: false,
            sessionRate: "",
        },
        validationSchema: Yup.object({
            firstName: Yup.string().trim().required("First name is required"),
            lastName: Yup.string().trim().required("Last name is required"),
            DOB: Yup.string().required("Date of birth is required"),
            gender: Yup.string().required("Gender is required"),
            email: Yup.string().email("Invalid email"),
            mobile: Yup.string().required("Mobile is required"),
            type: Yup.string().required("Employee type is required"),
            branch: Yup.string().nullable(),
            salaryCalculationMode: Yup.string().oneOf(["combined"]).required(),
            salaryType: Yup.string().oneOf(["fixed", "hourly", "percentage"]).required("Salary type is required"),
            salaryFixedEnabled: Yup.boolean(),
            salaryHourlyEnabled: Yup.boolean(),
            salaryPercentageEnabled: Yup.boolean(),
            salarySessionEnabled: Yup.boolean(),
            fixedSalary: Yup.number().when("salaryFixedEnabled", {
                is: true,
                then: (schema) => schema.min(0, "Fixed salary cannot be negative").required("Fixed salary is required"),
                otherwise: (schema) => schema.transform((value, originalValue) => originalValue === "" ? undefined : value).nullable(),
            }),
            hourlyRate: Yup.number().when("salaryHourlyEnabled", {
                is: true,
                then: (schema) => schema.min(0, "Hourly rate cannot be negative").required("Hourly rate is required"),
                otherwise: (schema) => schema.transform((value, originalValue) => originalValue === "" ? undefined : value).nullable(),
            }),
            commissionPercentage: Yup.number()
                .transform((value, originalValue) => originalValue === "" ? undefined : value)
                .nullable(),
            commissionRules: Yup.array().of(Yup.object({
                package: Yup.string().required("Package is required"),
                allocationId: Yup.string().test(
                    "activity-required",
                    "Activity is required for normal subscription commission",
                    function (value) {
                        const pkg = packages.find((item) => item._id === this.parent.package);
                        return !pkg?.revenueAllocations?.length || Boolean(value);
                    },
                ),
                percentage: Yup.number()
                    .typeError("Commission is required")
                    .min(0, "Commission cannot be negative")
                    .max(100, "Commission cannot exceed 100%")
                    .required("Commission is required"),
            })).when("salaryPercentageEnabled", {
                is: true,
                then: (schema) => schema
                    .min(1, "Assign at least one package for percentage commission")
                    .test("unique-packages", "Each package activity can only be assigned once", (rules) => {
                        const keys = (rules || [])
                            .map((rule) => rule?.package ? `${rule.package}:${rule.allocationId || "pt"}` : "")
                            .filter(Boolean);
                        return new Set(keys).size === keys.length;
                    }),
                otherwise: (schema) => schema,
            }),
            sessionDurationHours: Yup.number().when("salaryHourlyEnabled", {
                is: true,
                then: (schema) => schema.min(0, "Session hours cannot be negative").required("Session hours are required"),
                otherwise: (schema) => schema.transform((value, originalValue) => originalValue === "" ? undefined : value).nullable(),
            }),
            sessionRate: Yup.number().when("salarySessionEnabled", {
                is: true,
                then: (schema) => schema.min(0, "Session rate cannot be negative").required("Session rate is required"),
                otherwise: (schema) => schema.transform((value, originalValue) => originalValue === "" ? undefined : value).nullable(),
            }),
        }).test({
            name: "salary-method-required",
            message: "Select at least one salary method",
            test(values) {
                const hasMethod = Boolean(
                    values?.salaryFixedEnabled
                    || values?.salaryHourlyEnabled
                    || values?.salaryPercentageEnabled
                    || values?.salarySessionEnabled
                );
                return hasMethod || this.createError({ path: "salaryFixedEnabled" });
            },
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                if (!user) {
                    showErrorToast("User not logged in");
                    return;
                }

                const formData = new FormData();
                const payloadValues = {
                    ...values,
                    salaryCalculationMode: "combined",
                    salaryType: getPrimarySalaryType(values),
                    commissionPercentage: values.commissionRules[0]?.percentage ?? 0,
                };
                const { commissionRules, ...scalarPayloadValues } = payloadValues;
                Object.entries(scalarPayloadValues).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        formData.append(key, value as any);
                    }
                });
                formData.append(
                    "commissionRules",
                    JSON.stringify(values.salaryPercentageEnabled ? commissionRules : []),
                );
                if (imageFile) {
                    formData.append("profileImage", imageFile);
                }
                formData.append("birthday", values.DOB);

                if (editingEmployeeId) {
                    const updated = await updateEmployeeService(editingEmployeeId, formData);
                    const updatedId = updated?._id ?? updated?.id ?? editingEmployeeId;
                    setTableData((prev) =>
                        prev.map((emp) =>
                            (emp._id ?? emp.id) === updatedId ? { ...emp, ...updated } : emp
                        )
                    );
                } else {
                    const created = await addEmployeeService(formData);
                    const createdId = created?._id ?? created?.id;
                    setTableData((prev) => [{ ...created, _id: createdId }, ...prev]);
                }

                resetForm();
                setAddCoach(false);
                setEditingEmployeeId(null);
                setImageFile(null);
                setImagePreview(null);
            } catch (err) {
                console.error("Error adding/updating employee:", err);
                const message = (err as any)?.response?.data?.error || "Failed to save employee";
                showErrorToast(message);
            }
        },
    });

    const closePopUp = () => {
        setAddCoach(false);
        setEditingEmployeeId(null);
        formik.resetForm();
        removeImage();
    };
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!user?.id) return;
            try {
                const employees = await getEmployees();
                const normalized = employees.map((e: any) => ({ ...e, _id: e._id ?? e.id }));
                setTableData(normalized);
            } catch (err) {
                console.error("Failed to fetch employees", err);
            }
        };
        const fetchBranches = async () => {
            if (!user?.id) return;
            try {
                const branches = await getBranchService();
                setBranchesType(branches)

            }
            catch (err) {
                console.error("Failed to fetch branches", err)
            }
        }
        const fetchCoachType = async () => {
            if (!user?.id) return;
            try {
                const coachTypes = await getCoachTypeService();
                setCoachTypes(coachTypes)
            }
            catch (err) {
                console.error("Failed to fetch coach type", err)
            }
        }
        const fetchPackages = async () => {
            if (!user?.id) return;
            try {
                const packageRows = await getPackages();
                const selectablePackages = (Array.isArray(packageRows) ? packageRows : [])
                    .map((pkg: PackageOption) => ({
                        _id: pkg._id,
                        name: pkg.name,
                        status: pkg.status,
                        isPtSession: pkg.isPtSession,
                        numberOfPtSessions: pkg.numberOfPtSessions,
                        revenueAllocations: Array.isArray(pkg.revenueAllocations) ? pkg.revenueAllocations : [],
                    }));
                setPackages(selectablePackages);
            }
            catch (err) {
                console.error("Failed to fetch packages", err);
            }
        }

        fetchEmployees();
        fetchBranches();
        fetchCoachType();
        fetchPackages();

    }, [user?.id])

    const buildImageUrl = (url?: string) => {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) return url;
        return url.startsWith("/") ? `${API_ORIGIN}${url}` : `${API_ORIGIN}/${url}`;
    };

    const handleEditEmployee = (employee: any) => {
        if (!employee) return;
        setEditingEmployeeId(employee._id ?? employee.id ?? null);
        const profileUrl = buildImageUrl(employee?.profileImage?.url ?? employee?.profileImage ?? "");
        setImagePreview(profileUrl || null);
        const salaryComponents = getSalaryComponents(employee);

        formik.setValues({
            firstName: employee.firstName ?? "",
            lastName: employee.lastName ?? "",
            DOB: (employee.DOB ?? employee.birthday) ? (employee.DOB ?? employee.birthday).split("T")[0] : "",
            gender: employee.gender ?? "",
            bloodType: employee.bloodType ?? "",
            branch: employee.branch?._id ?? employee.branch ?? "",
            email: employee.email ?? "",
            mobile: employee.mobile ?? "",
            emergencyContact: employee.emergencyContact ?? "",
            address: employee.address ?? "",
            status: Boolean(employee.status),
            type: employee.type?._id ?? employee.type ?? "",
            salaryCalculationMode: "combined",
            salaryType: getPrimarySalaryType({
                salaryFixedEnabled: salaryComponents.fixed,
                salaryHourlyEnabled: salaryComponents.hourly,
                salaryPercentageEnabled: salaryComponents.percentage,
            }),
            salaryFixedEnabled: salaryComponents.fixed,
            fixedSalary: employee.fixedSalary ?? "",
            salaryHourlyEnabled: salaryComponents.hourly,
            hourlyRate: employee.hourlyRate ?? "",
            salaryPercentageEnabled: salaryComponents.percentage,
            commissionPercentage: employee.commissionPercentage ?? "",
            commissionRules: Array.isArray(employee.commissionRules)
                ? employee.commissionRules.map((rule: any) => ({
                    package: rule?.package?._id ?? rule?.package ?? "",
                    allocationId: rule?.allocationId ?? "",
                    percentage: rule?.percentage ?? "",
                }))
                : [],
            sessionDurationHours: employee.sessionDurationHours ?? "1",
            salarySessionEnabled: salaryComponents.session,
            sessionRate: employee.sessionRate ?? "",
        });

        setAddCoach(true);
    };

    const selectedCoachSalaryComponents = getSalaryComponents(selectedCoach);
    const selectedCoachCommissionRules = Array.isArray(selectedCoach?.commissionRules)
        ? selectedCoach.commissionRules
        : [];
    const selectedCoachProfileUrl = buildImageUrl(
        selectedCoach?.profileImage?.url ?? selectedCoach?.profileImage ?? "",
    );

    const getCommissionRuleDetails = (rule: any) => {
        const rulePackage = rule?.package && typeof rule.package === "object"
            ? rule.package
            : packages.find((pkg) => String(pkg._id) === String(rule?.package));
        const allocation = rulePackage?.revenueAllocations?.find(
            (item: any) => String(item?._id) === String(rule?.allocationId),
        );

        return {
            packageName: rulePackage?.name ?? "Package",
            allocationName: allocation?.name ?? (rule?.allocationId ? "Activity" : "All package revenue"),
        };
    };

    return (
        <div className="p-6 w-full">
            <h1 className="text-2xl font-bold mb-4">Coaches</h1>

            <Table
                columns={columns}
                data={tableData}
                pageSize={5}
                add={canEditEmployees}
                onOpen={openPopUp}
            />

            <AddDialog
                title="Coach Details"
                isOpen={Boolean(selectedCoach)}
                onClose={() => setSelectedCoach(null)}
                maxWidth="max-w-4xl"
            >
                {selectedCoach && (
                    <div className="space-y-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-3xl font-semibold text-gray-500">
                                {selectedCoachProfileUrl ? (
                                    <img
                                        src={selectedCoachProfileUrl}
                                        alt={`${selectedCoach.firstName ?? ""} ${selectedCoach.lastName ?? ""}`.trim()}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <span>
                                        {`${selectedCoach.firstName?.[0] ?? ""}${selectedCoach.lastName?.[0] ?? ""}` || "?"}
                                    </span>
                                )}
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold text-gray-900">
                                    {[selectedCoach.firstName, selectedCoach.lastName].filter(Boolean).join(" ") || "Unnamed coach"}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {selectedCoach?.type?.name ?? selectedCoach?.type ?? "Coach"}
                                </p>
                                <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${selectedCoach.status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                    {selectedCoach.status ? "Active" : "Inactive"}
                                </span>
                            </div>
                        </div>

                        <section className="rounded-lg border border-gray-200 p-5">
                            <h4 className="mb-4 font-semibold text-gray-900">Personal and employment information</h4>
                            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                <DetailItem label="Date of birth" value={formatDate(selectedCoach.DOB ?? selectedCoach.birthday)} />
                                <DetailItem label="Gender" value={selectedCoach.gender} />
                                <DetailItem label="Blood type" value={selectedCoach.bloodType} />
                                <DetailItem label="Employee type" value={selectedCoach?.type?.name ?? selectedCoach?.type} />
                                <DetailItem label="Branch" value={selectedCoach?.branch?.name ?? selectedCoach?.branch} />
                            </dl>
                        </section>

                        <section className="rounded-lg border border-gray-200 p-5">
                            <h4 className="mb-4 font-semibold text-gray-900">Contact information</h4>
                            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <DetailItem label="Email" value={selectedCoach.email} />
                                <DetailItem label="Mobile" value={selectedCoach.mobile} />
                                <DetailItem label="Emergency contact" value={selectedCoach.emergencyContact} />
                                <DetailItem label="Address" value={selectedCoach.address} />
                            </dl>
                        </section>

                        <section className="rounded-lg border border-gray-200 p-5">
                            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                                <h4 className="font-semibold text-gray-900">Salary details</h4>
                                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                    {formatSalaryMethods(selectedCoach)}
                                </span>
                            </div>

                            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                                {selectedCoachSalaryComponents.fixed && (
                                    <DetailItem label="Fixed salary" value={formatMoney(selectedCoach.fixedSalary)} />
                                )}
                                {selectedCoachSalaryComponents.hourly && (
                                    <>
                                        <DetailItem label="Hourly rate" value={formatMoney(selectedCoach.hourlyRate)} />
                                        <DetailItem label="Hours per session" value={formatMoney(selectedCoach.sessionDurationHours)} />
                                    </>
                                )}
                                {selectedCoachSalaryComponents.session && (
                                    <DetailItem label="Rate per session" value={formatMoney(selectedCoach.sessionRate)} />
                                )}
                                {selectedCoachSalaryComponents.percentage && selectedCoachCommissionRules.length === 0 && (
                                    <DetailItem
                                        label="Commission percentage"
                                        value={`${formatMoney(selectedCoach.commissionPercentage)}%`}
                                    />
                                )}
                            </dl>

                            {selectedCoachSalaryComponents.percentage && selectedCoachCommissionRules.length > 0 && (
                                <div className="mt-5 overflow-x-auto">
                                    <h5 className="mb-2 text-sm font-semibold text-gray-800">Package commissions</h5>
                                    <table className="w-full min-w-[520px] border-collapse text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-gray-500">
                                                <th className="px-3 py-2 font-medium">Package</th>
                                                <th className="px-3 py-2 font-medium">Activity</th>
                                                <th className="px-3 py-2 text-right font-medium">Commission</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedCoachCommissionRules.map((rule: any, index: number) => {
                                                const details = getCommissionRuleDetails(rule);
                                                return (
                                                    <tr key={rule?._id ?? `${rule?.package ?? "package"}-${rule?.allocationId ?? index}`} className="border-b border-gray-100 last:border-0">
                                                        <td className="px-3 py-2 text-gray-900">{details.packageName}</td>
                                                        <td className="px-3 py-2 text-gray-700">{details.allocationName}</td>
                                                        <td className="px-3 py-2 text-right font-medium text-gray-900">
                                                            {formatMoney(rule?.percentage)}%
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </AddDialog>

            <AddDialog
                title="Add New Employee"
                isOpen={addCoach}
                onClose={closePopUp}
                maxWidth="max-w-5xl"
            >
                <div className="p-6">
                    <form onSubmit={formik.handleSubmit} className="space-y-8">
                        {/* Profile Picture */}
                        <div className="flex justify-center mb-8">
                            <div className="relative">
                                <div className="w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                                    {imagePreview ? (
                                        <img
                                            src={imagePreview}
                                            alt="Profile preview"
                                            className="w-full h-full object-cover rounded-full"
                                        />
                                    ) : (
                                        <div className="text-center">
                                            <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            <p className="text-xs text-gray-500">Profile Picture</p>
                                        </div>
                                    )}
                                </div>
                                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                                    <label className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-full text-xs cursor-pointer transition-colors">
                                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                                        {imagePreview ? "Change" : "Upload"}
                                    </label>
                                    {imagePreview && (
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-full text-xs transition-colors"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 2x2 Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Personal Information */}
                            <div className="bg-gray-50 p-6 rounded-lg h-fit">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    Personal Information
                                </h3>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <TextInput
                                                label="First Name"
                                                type="text"
                                                name="firstName"
                                                value={formik.values.firstName}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="Enter first name"
                                            />
                                            {formik.touched.firstName && formik.errors.firstName && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.firstName}</p>
                                            )}
                                        </div>

                                        <div>
                                            <TextInput
                                                label="Last Name"
                                                type="text"
                                                name="lastName"
                                                value={formik.values.lastName}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="Enter last name"
                                            />
                                            {formik.touched.lastName && formik.errors.lastName && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.lastName}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <TextInput
                                                label="Date of Birth"
                                                type="date"
                                                name="DOB"
                                                value={formik.values.DOB}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            {formik.touched.DOB && formik.errors.DOB && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.DOB}</p>
                                            )}
                                        </div>

                                        <div>
                                            <SelectInput
                                                label="Gender"
                                                name="gender"
                                                value={formik.values.gender}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                options={[
                                                    { value: "Male", label: "Male" },
                                                    { value: "Female", label: "Female" },
                                                ]}
                                            />
                                            {formik.touched.gender && formik.errors.gender && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.gender}</p>
                                            )}
                                        </div>
                                    </div>

                                    <SelectInput
                                        label="Blood Type"
                                        name="bloodType"
                                        value={formik.values.bloodType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        options={[
                                            { value: "A+", label: "A+" },
                                            { value: "A-", label: "A-" },
                                            { value: "B+", label: "B+" },
                                            { value: "B-", label: "B-" },
                                            { value: "AB+", label: "AB+" },
                                            { value: "AB-", label: "AB-" },
                                            { value: "O+", label: "O+" },
                                            { value: "O-", label: "O-" },
                                        ]}
                                    />
                                    {formik.touched.bloodType && formik.errors.bloodType && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.bloodType}</p>
                                    )}

                                    <div className="space-y-4">
                                        <SelectInput
                                            label="Employee Type"
                                            name="type"
                                            value={formik.values.type}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            options={coachTypes.map((type) => ({
                                                value: type._id,
                                                label: type.name,
                                            }))}
                                        />
                                        {formik.touched.type && formik.errors.type && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.type}</p>
                                        )}

                                        <SelectInput
                                            label="Branch"
                                            name="branch"
                                            value={formik.values.branch}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            options={branches.map((branch) => ({
                                                value: branch._id,
                                                label: branch.name,
                                            }))}
                                        />
                                        {formik.touched.branch && formik.errors.branch && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.branch}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="bg-gray-50 p-6 rounded-lg h-fit">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    Contact Information
                                </h3>

                                <div className="space-y-4">
                                    <TextInput
                                        label="Email Address"
                                        type="email"
                                        name="email"
                                        placeholder="Enter email address"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
                                    )}

                                    <TextInput
                                        label="Mobile Number"
                                        type="tel"
                                        name="mobile"
                                        placeholder="Enter mobile number"
                                        value={formik.values.mobile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.mobile && formik.errors.mobile && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.mobile}</p>
                                    )}

                                    <TextInput
                                        label="Emergency Contact"
                                        type="tel"
                                        name="emergencyContact"
                                        placeholder="Enter emergency contact"
                                        value={formik.values.emergencyContact}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.emergencyContact && formik.errors.emergencyContact && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.emergencyContact}</p>
                                    )}

                                    <TextInput
                                        label="Full Address"
                                        type="text"
                                        name="address"
                                        placeholder="Enter complete address"
                                        value={formik.values.address}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.address && formik.errors.address && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.address}</p>
                                    )}
                                </div>
                            </div>

                            {/* Salary Information */}
                            <div className="bg-gray-50 p-6 rounded-lg h-fit lg:col-span-2">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-[#ac120c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                    </svg>
                                    Salary Information
                                </h3>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium">
                                            <input
                                                type="checkbox"
                                                name="salaryFixedEnabled"
                                                checked={formik.values.salaryFixedEnabled}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            Fixed
                                        </label>
                                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium">
                                            <input
                                                type="checkbox"
                                                name="salaryHourlyEnabled"
                                                checked={formik.values.salaryHourlyEnabled}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            Hourly
                                        </label>
                                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium">
                                            <input
                                                type="checkbox"
                                                name="salaryPercentageEnabled"
                                                checked={formik.values.salaryPercentageEnabled}
                                                onChange={(event) => {
                                                    const enabled = event.target.checked;
                                                    formik.setFieldValue("salaryPercentageEnabled", enabled);
                                                    if (enabled && formik.values.commissionRules.length === 0) {
                                                        formik.setFieldValue("commissionRules", [{ package: "", allocationId: "", percentage: "" }]);
                                                    }
                                                }}
                                                onBlur={formik.handleBlur}
                                            />
                                            Percentage
                                        </label>
                                        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium">
                                            <input
                                                type="checkbox"
                                                name="salarySessionEnabled"
                                                checked={formik.values.salarySessionEnabled}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            Per Session
                                        </label>
                                    </div>
                                    {formik.errors.salaryFixedEnabled && (
                                        <p className="text-red-500 text-sm">{formik.errors.salaryFixedEnabled}</p>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {formik.values.salaryFixedEnabled && (
                                        <div>
                                            <TextInput
                                                label="Fixed Salary"
                                                type="number"
                                                name="fixedSalary"
                                                min="0"
                                                step="0.01"
                                                value={formik.values.fixedSalary}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                            />
                                            {formik.touched.fixedSalary && formik.errors.fixedSalary && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.fixedSalary}</p>
                                            )}
                                        </div>
                                    )}

                                    {formik.values.salaryHourlyEnabled && (
                                        <>
                                            <div>
                                                <TextInput
                                                    label="Hourly Rate"
                                                    type="number"
                                                    name="hourlyRate"
                                                    min="0"
                                                    step="0.01"
                                                    value={formik.values.hourlyRate}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="0.00"
                                                />
                                                {formik.touched.hourlyRate && formik.errors.hourlyRate && (
                                                    <p className="text-red-500 text-sm mt-1">{formik.errors.hourlyRate}</p>
                                                )}
                                            </div>
                                            <div>
                                                <TextInput
                                                    label="Hours Per Session"
                                                    type="number"
                                                    name="sessionDurationHours"
                                                    min="0"
                                                    step="0.25"
                                                    value={formik.values.sessionDurationHours}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="1"
                                                />
                                                {formik.touched.sessionDurationHours && formik.errors.sessionDurationHours && (
                                                    <p className="text-red-500 text-sm mt-1">{formik.errors.sessionDurationHours}</p>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {formik.values.salaryPercentageEnabled && (
                                        <div className="space-y-3 md:col-span-2">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Package commissions</p>
                                                    <p className="text-xs text-gray-500">PT commission uses delivered sessions. Normal package commission uses the activity&apos;s allocated subscription revenue.</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => formik.setFieldValue("commissionRules", [
                                                        ...formik.values.commissionRules,
                                                        { package: "", allocationId: "", percentage: "" },
                                                    ])}
                                                    className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50"
                                                >
                                                    Add package
                                                </button>
                                            </div>

                                            {formik.values.commissionRules.map((rule, index) => {
                                                const ruleErrors = (formik.errors.commissionRules as any)?.[index];
                                                const selectedPackage = packages.find((pkg) => pkg._id === rule.package);
                                                const selectedActivities = formik.values.commissionRules
                                                    .filter((item, ruleIndex) => ruleIndex !== index && item.package === rule.package)
                                                    .map((item) => item.allocationId);

                                                return (
                                                    <div key={index} className="rounded-md border border-gray-200 bg-white p-3">
                                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_160px_auto] md:items-end">
                                                            <div className="flex flex-col gap-1">
                                                                <label className="text-sm font-medium">Package</label>
                                                                <select
                                                                    name={`commissionRules.${index}.package`}
                                                                    value={rule.package}
                                                                    onChange={(event) => {
                                                                        formik.handleChange(event);
                                                                        formik.setFieldValue(`commissionRules.${index}.allocationId`, "");
                                                                    }}
                                                                    onBlur={formik.handleBlur}
                                                                    className="rounded-md border border-gray-300 px-3 py-2"
                                                                >
                                                                    <option value="">Select package</option>
                                                                    {packages.map((pkg) => (
                                                                        <option
                                                                            key={pkg._id}
                                                                            value={pkg._id}
                                                                            disabled={!pkg.isPtSession && !pkg.revenueAllocations?.length}
                                                                        >
                                                                            {pkg.name}
                                                                            {pkg.status === false ? " (Inactive)" : ""}
                                                                            {!pkg.isPtSession && !pkg.revenueAllocations?.length ? " (Configure revenue split first)" : ""}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {ruleErrors?.package && formik.submitCount > 0 && (
                                                                    <p className="text-sm text-red-500">{ruleErrors.package}</p>
                                                                )}
                                                            </div>
                                                            {selectedPackage?.revenueAllocations?.length ? (
                                                                <div className="flex flex-col gap-1">
                                                                    <label className="text-sm font-medium">Activity</label>
                                                                    <select
                                                                        name={`commissionRules.${index}.allocationId`}
                                                                        value={rule.allocationId}
                                                                        onChange={formik.handleChange}
                                                                        onBlur={formik.handleBlur}
                                                                        className="rounded-md border border-gray-300 px-3 py-2"
                                                                    >
                                                                        <option value="">Select activity</option>
                                                                        {selectedPackage.revenueAllocations.map((allocation) => (
                                                                            <option
                                                                                key={allocation._id}
                                                                                value={allocation._id}
                                                                                disabled={selectedActivities.includes(allocation._id)}
                                                                            >
                                                                                {allocation.name} ({allocation.percentage}% of package)
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {ruleErrors?.allocationId && formik.submitCount > 0 && (
                                                                        <p className="text-sm text-red-500">{ruleErrors.allocationId}</p>
                                                                    )}
                                                                </div>
                                                            ) : null}
                                                            <div>
                                                                <TextInput
                                                                    label="Commission %"
                                                                    type="number"
                                                                    name={`commissionRules.${index}.percentage`}
                                                                    min="0"
                                                                    max="100"
                                                                    step="0.01"
                                                                    value={rule.percentage}
                                                                    onChange={formik.handleChange}
                                                                    onBlur={formik.handleBlur}
                                                                    placeholder="0"
                                                                />
                                                                {ruleErrors?.percentage && formik.submitCount > 0 && (
                                                                    <p className="mt-1 text-sm text-red-500">{ruleErrors.percentage}</p>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => formik.setFieldValue(
                                                                    "commissionRules",
                                                                    formik.values.commissionRules.filter((_, ruleIndex) => ruleIndex !== index),
                                                                )}
                                                                className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {typeof formik.errors.commissionRules === "string" && formik.submitCount > 0 && (
                                                <p className="text-sm text-red-500">{formik.errors.commissionRules}</p>
                                            )}
                                            {packages.length === 0 && (
                                                <p className="text-sm text-amber-700">No packages are available. Create a package before assigning percentage commission.</p>
                                            )}
                                        </div>
                                    )}

                                    {formik.values.salarySessionEnabled && (
                                        <div>
                                            <TextInput
                                                label="Rate Per Session"
                                                type="number"
                                                name="sessionRate"
                                                min="0"
                                                step="0.01"
                                                value={formik.values.sessionRate}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                            />
                                            {formik.touched.sessionRate && formik.errors.sessionRate && (
                                                <p className="text-red-500 text-sm mt-1">{formik.errors.sessionRate}</p>
                                            )}
                                        </div>
                                    )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={closePopUp}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors shadow-md"
                            >
                                {editingEmployeeId ? "Update Employee" : "Add Employee"}
                            </button>
                        </div>
                    </form>
                </div>
            </AddDialog>
        </div>
    );
};

export default Coaches;
